import React from "react";
import PropTypes from "prop-types";
import "./SharedTable.css";
import { Button } from "../ui/Button";
import EditIcon from "./EditIcon.js";
import TrashIcon from "./TrashIcon.js";

const formatPhoneNumber = (value) => {
  if (!value) return value;
  const phoneNumber = value.replaceAll(/\D/g, "");
  const phoneNumberLength = phoneNumber.length;
  if (phoneNumberLength < 4) return phoneNumber;
  if (phoneNumberLength < 7) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  }
  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(
    3,
    6,
  )}-${phoneNumber.slice(6, 10)}`;
};

const getPersonInitials = (person) => {
  if (person.first_name && person.last_name) {
    return (person.first_name[0] + person.last_name[0]).toUpperCase();
  }
  if (person.first_name) {
    return person.first_name.slice(0, 2).toUpperCase();
  }
  if (person.name) {
    const parts = person.name.split(" ").filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts.at(-1)[0]).toUpperCase();
    }
    return parts[0].slice(0, 2).toUpperCase();
  }
  if (person.email) {
    return person.email[0].toUpperCase();
  }
  return "U";
};

const resolveNameHeader = (type, renderRoleColumn) => {
  if (renderRoleColumn) return "Name";
  if (type === "Player" || type === "Person" || type === "Member") {
    return "Name";
  }
  return type;
};

const PersonList = ({
  people,
  type = "Person",
  onEdit,
  onRemove,
  canEdit,
  renderBadges = () => null,
  renderRoleColumn = null,
  showContactColumn = true,
  emptyMessage = "No people added yet.",
}) => {
  if (!people || people.length === 0) {
    return <div className="empty-state">{emptyMessage}</div>;
  }

  return (
    <div className="standard-table-container" style={{ marginTop: "0.75rem" }}>
      <table className="standard-table">
        <thead>
          <tr>
            <th>{resolveNameHeader(type, renderRoleColumn)}</th>
            {renderRoleColumn && <th>Role</th>}
            {showContactColumn && <th>Contact</th>}
            {canEdit && <th style={{ textAlign: "right" }}></th>}
          </tr>
        </thead>
        <tbody>
          {people.map((person, idx) => (
            <tr key={person.id || person.person_id || idx}>
              <td>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "6px 0",
                  }}
                >
                  <div className="initials-bubble">
                    {getPersonInitials(person)}
                  </div>
                  <strong style={{ fontSize: "1.02rem", fontWeight: 600 }}>
                    {person.first_name} {person.last_name}
                  </strong>
                  {renderBadges(person)}
                </div>
              </td>
              {renderRoleColumn && (
                <td>
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "6px 0",
                    }}
                  >
                    {renderRoleColumn(person)}
                  </div>
                </td>
              )}
              {showContactColumn && (
                <td>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                      fontSize: "0.9rem",
                      padding: "6px 0",
                    }}
                  >
                    <span>{person.email || ""}</span>
                    {(person.phone || person.phone_number) && (
                      <span style={{ color: "var(--color-text-secondary)" }}>
                        {formatPhoneNumber(person.phone || person.phone_number)}
                      </span>
                    )}
                  </div>
                </td>
              )}
              {canEdit && (
                <td style={{ textAlign: "right" }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      gap: "12px",
                    }}
                  >
                    {onEdit && (
                      <Button
                        variant="ghost"
                        size="icon"
                        type="button"
                        onClick={() => onEdit(person)}
                        title="Edit Person"
                      >
                        <EditIcon size="sm" />
                      </Button>
                    )}
                    {onRemove && person.canRemove !== false && (
                      <Button
                        variant="destructive"
                        size="icon"
                        type="button"
                        onClick={() => onRemove(person)}
                        title="Remove Person"
                      >
                        <TrashIcon size="sm" />
                      </Button>
                    )}
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

PersonList.propTypes = {
  people: PropTypes.array.isRequired,
  type: PropTypes.string,
  onEdit: PropTypes.func,
  onRemove: PropTypes.func,
  canEdit: PropTypes.bool,
  renderBadges: PropTypes.func,
  renderRoleColumn: PropTypes.func,
  showContactColumn: PropTypes.bool,
  emptyMessage: PropTypes.string,
};

export { formatPhoneNumber, PersonList };
